import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Form, Button, Tab, Tabs, Alert, OverlayTrigger, Popover } from 'react-bootstrap';
// 1. Remove Table from react-bootstrap imports and import from antd
import { Table as AntTable, Select, message, AutoComplete, Input, Popover as AntPopover } from 'antd';
import { searchTradeData, searchHscodeOnly, searchHscodeAutocomplete } from '../../../services/TradeService';
import ShimmerComponent from '../extra/ShimmerComponent';
import { useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';

const CollapsibleCell = ({ title, content }) => {
    const [open, setOpen] = useState(false);

    const hide = () => setOpen(false);
    const handleOpenChange = (newOpen) => setOpen(newOpen);

    const popoverContent = (
        <div style={{ maxWidth: '400px', maxHeight: '300px', overflowY: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: content }} />
            <div style={{ textAlign: 'right', marginTop: '10px' }}>
                <Button size="sm" variant="secondary" onClick={hide}><span >x</span></Button>
            </div>
        </div>
    );

    return (
        <AntPopover
            content={popoverContent}
            title={title}
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            placement="right"
        >
            <Button variant="primary" size="sm" onClick={() => setOpen(true)}>{title}</Button>
        </AntPopover>
    );
};

const TradeSearch = () => {
    const location = useLocation();

    const [hscode, setHscode] = useState('');
    const [hscodeOptions, setHscodeOptions] = useState([]);
    const [assessableValue, setAssessableValue] = useState('');
    const [igstRate, setIgstRate] = useState(null);
    const [igstOptions, setIgstOptions] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');



    // Initialize from navigation state if present
    useEffect(() => {
        if (location.state) {
            const { hscode: navHscode, assessableValue: navAssessableValue, generalData, fullSearchResult } = location.state;

            if (navHscode) {
                setHscode(navHscode);
            }
            if (navAssessableValue) {
                setAssessableValue(navAssessableValue);
            }
            if (fullSearchResult) {
                // Full search result from modal when assessable value was provided
                setResults(fullSearchResult);
                extractIgstOptions(fullSearchResult);
            } else if (generalData) {
                setResults(generalData);
                // Extract IGST options from General Data
                extractIgstOptions(generalData);
            }
        }
    }, [location.state]);

    // Extract IGST options from General Data
    const extractIgstOptions = (data) => {
        if (!Array.isArray(data)) return;

        const generalDataSection = data.find(r => r.title === 'General Data');
        if (!generalDataSection || !generalDataSection.data) return;

        const igstValues = new Set();
        generalDataSection.data.forEach(row => {
            // Look for IGST column in any format
            Object.keys(row).forEach(key => {
                if (key.toLowerCase().includes('igst')) {
                    const cellData = row[key];
                    const value = typeof cellData === 'object' ? cellData.text : cellData;
                    if (value) {
                        // Split by comma to handle multiple IGST values like "0 % (Schedule VII) , 0 % (Schedule VI)"
                        const splitValues = value.split(',').map(v => v.trim()).filter(v => v);
                        splitValues.forEach(v => igstValues.add(v));
                    }
                }
            });
        });

        const options = Array.from(igstValues).map(val => {
            // Extract just the number from strings like "28 % (Schedule VII)"
            const numericMatch = val.match(/^(\d+(?:\.\d+)?)/);
            const numericValue = numericMatch ? numericMatch[1] : val;
            return {
                value: numericValue,
                label: val
            };
        });
        setIgstOptions(options);
    };

    // Debounced autocomplete search for HS code
    const fetchHscodeOptions = useCallback(
        debounce(async (searchText) => {
            if (!searchText || searchText.length < 2) {
                setHscodeOptions([]);
                return;
            }
            try {
                const results = await searchHscodeAutocomplete(searchText);
                // Convert results to options format
                const options = results.map(item => ({
                    value: item,
                    label: item
                }));
                setHscodeOptions(options);
            } catch (error) {
                console.error('Autocomplete error:', error);
                setHscodeOptions([]);
            }
        }, 300),
        []
    );

    // Handle HS code input change
    const handleHscodeChange = (value) => {
        setHscode(value);
        fetchHscodeOptions(value);
    };

    // Handle HS code selection from autocomplete
    const handleHscodeSelect = (value) => {
        setHscode(value);
        setHscodeOptions([]);
    };

    // Handle HS Code only search
    const handleHscodeSearch = async () => {
        if (!hscode) {
            setError("Please enter HS Code");
            return;
        }


        setLoading(true);
        setError(null);

        try {
            const data = await searchHscodeOnly(hscode);
            setResults(data);
            extractIgstOptions(data);
            // Reset assessable value and IGST when doing HS code search
            setAssessableValue('');
            setIgstRate(null);
        } catch (err) {
            setError(typeof err === 'string' ? err : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Handle full search with assessable value and optional IGST
    const handleFullSearch = async (e) => {
        e.preventDefault();
        if (!hscode || !assessableValue) {
            setError("Please provide both HS Code and Assessable Value");
            return;
        }


        setLoading(true);
        setError(null);

        try {
            const data = await searchTradeData(hscode, assessableValue, igstRate);
            setResults(data);
            extractIgstOptions(data);
        } catch (err) {
            setError(typeof err === 'string' ? err : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };



    // Helper to determine column width based on Header Name
    const getColumnWidth = (key) => {
        const lowerKey = key.toLowerCase();
        // Small columns for numbers/indexes
        if (['sr.no', 'uom', 'duty(%)'].some(k => lowerKey.includes(k)) && key.length < 10) {
            return 60;
        }
        if (['duty'].some(k => lowerKey.includes(k)) && key.length < 10) {
            return 100;
        }
        // Large columns for descriptions
        if (lowerKey.includes('desc') || lowerKey.includes('detail') || lowerKey.includes('title')) {
            return 400;
        }

        if (['hs code'].some(k => lowerKey.includes(k)) && key.length < 10) {
            return 600;
        }
        // Medium default
        return 160;
    };

    const renderCellContent = (cellData, uniqueKey, columnKey) => {
        if (!cellData || typeof cellData !== 'object') return cellData;

        // Base content (Text or Link)
        let content = cellData.text;

        if (cellData.type === 'link') {
            content = (
                <a
                    href={cellData.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm"
                    style={{ color: 'blue' }}
                >
                    {cellData.text || 'View PDF'}
                </a>
            );
        } else if (cellData.type === 'collapse') {
            // 4. Check if it's the HS Code column
            const isHsCodeColumn = columnKey && String(columnKey).toLowerCase().includes('hs code');

            if (isHsCodeColumn) {
                // Just display the text, no collapse/modal. Ensure HTML is rendered.
                // Sometimes the content is in 'text' (if transformed) or 'collapse' (if raw).
                // Use whatever is available.
                const htmlContent = cellData.collapse || cellData.text;
                return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
            }

            const btnText = cellData.collapse_btn_text || 'Show Details';
            // Use locally defined CollapsibleCell component for popover behavior
            return <CollapsibleCell title={btnText} content={cellData.collapse} />;
        }

        // Wrap in Popover if exists
        if (cellData.popover) {
            const popover = (
                <Popover id={`popover-${uniqueKey}`}>
                    <Popover.Body dangerouslySetInnerHTML={{ __html: cellData.popover }} />
                </Popover>
            );

            return (
                <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={popover}>
                    <span style={{ cursor: 'pointer', textDecoration: 'underline dotted', overflowWrap: 'anywhere' }}>{content}</span>
                </OverlayTrigger>
            );
        }

        return content;
    };

    // Filter rows based on search text
    const filterRows = (rows) => {
        if (!filterText || !rows) return rows;
        const searchLower = filterText.toLowerCase();
        return rows.filter(row => {
            return Object.values(row).some(cellData => {
                const text = typeof cellData === 'object' ? cellData?.text : cellData;
                return text && String(text).toLowerCase().includes(searchLower);
            });
        });
    };

    const renderTable = (rows, sectionIdx, tabIdx = 'x') => {
        const filteredRows = filterRows(rows);
        if (!filteredRows || filteredRows.length === 0) return <Alert variant="light">No Data Found</Alert>;

        // 2. Dynamically generate Ant Design Columns
        const columns = Object.keys(rows[1] || rows[0]).map((key) => {
            let title = key;
            if (key === 'Serial Number') {
                title = 'Item Sr. No.';
            }

            return {
                title: title,
                dataIndex: key,
                key: key,
                width: getColumnWidth(key), // Use helper to set intelligent widths
                render: (text, record, index) => {
                    // Generate a unique key for state management (collapses/popovers)
                    // Note: Using 'key' in the ID so we know which column it belongs to
                    const uniqueKey = `${sectionIdx}-${tabIdx}-${index}-${key}`;
                    return (
                        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {renderCellContent(text, uniqueKey, key)}
                        </div>
                    );
                }
            };
        });

        // 3. Render Ant Design Table
        return (
            <AntTable
                dataSource={filteredRows.map((row, i) => ({ ...row, key: i }))} // Ensure rows have unique keys
                columns={columns}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content', y: 500 }} // Enable horizontal scroll for wide tables, vertical for height
                style={{ marginTop: 10 }}
            />
        );
    };

    return (
        <>
            {/* 1. Extract General Data & Other Results */}
            {(() => {
                const generalData = results && Array.isArray(results) ? results.find(r => r.title === 'General Data') : null;
                const otherResults = results && Array.isArray(results) ? results.filter(r => r.title !== 'General Data') : [];

                return (
                    <Row>
                        <Col lg="12">
                            <Card>
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <div className="header-title">
                                        <h4 className="card-title">Import Duty Rate</h4>
                                    </div>
                                    {results && (
                                        <div style={{ width: '300px' }}>
                                            <Input
                                                placeholder="Filter results..."
                                                value={filterText}
                                                onChange={(e) => setFilterText(e.target.value)}
                                                allowClear
                                                prefix={<span style={{ color: '#999' }}>🔍</span>}
                                            />
                                        </div>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {/* 2. Render General Data Table Above Inputs */}
                                    {generalData && (
                                        <div className="mb-4">
                                            {renderTable(generalData.data, 'general_data')}
                                        </div>
                                    )}

                                    <Form onSubmit={handleFullSearch}>
                                        <Row className="align-items-end">
                                            <Col md="3">
                                                <Form.Group className="form-group mb-2">
                                                    <Form.Label htmlFor="hscode">HS Code</Form.Label>
                                                    <AutoComplete
                                                        style={{ width: '100%' }}
                                                        options={hscodeOptions}
                                                        value={hscode}
                                                        onChange={handleHscodeChange}
                                                        onSelect={handleHscodeSelect}
                                                    >
                                                        <Input id="hscode" placeholder="Enter 8-digit HS Code" />
                                                    </AutoComplete>
                                                </Form.Group>
                                            </Col>
                                            <Col md="2" className="mb-2">
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleHscodeSearch}
                                                    disabled={loading || !hscode}
                                                >
                                                    {loading ? 'Searching...' : 'Search HS Code'}
                                                </Button>
                                            </Col>
                                            <Col md="2">
                                                <Form.Group className="form-group mb-2">
                                                    <Form.Label htmlFor="assessableValue">Assessable Value</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        id="assessableValue"
                                                        placeholder="Enter Value"
                                                        value={assessableValue}
                                                        onChange={(e) => setAssessableValue(e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md="3">
                                                <Form.Group className="form-group mb-2">
                                                    <Form.Label>Select IGST for Benefits</Form.Label>
                                                    <Select
                                                        style={{ width: '100%' }}
                                                        placeholder="Select IGST Rate"
                                                        value={igstRate}
                                                        onChange={(value) => setIgstRate(value)}
                                                        options={igstOptions}
                                                        allowClear
                                                        disabled={igstOptions.length === 0}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md="2" className="mb-2">
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={loading || !hscode || !assessableValue}
                                                >
                                                    {loading ? 'Searching...' : 'Calculate Duty'}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>
                                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                );
            })()}

            {loading && (
                <Row>
                    <Col><ShimmerComponent /></Col>
                </Row>
            )}

            {results && !loading && Array.isArray(results) && results.filter(r => r.title !== 'General Data').length > 0 && (
                <Row>
                    <Col lg="12">
                        <Card>
                            <Card.Body>
                                <Tabs defaultActiveKey={results.filter(r => r.title !== 'General Data')[0]?.title} id="trade-results-tabs" className="mb-3" fill>
                                    {results.filter(r => r.title !== 'General Data').map((section, idx) => (
                                        <Tab eventKey={section.title} title={<span style={{ fontWeight: 'bold' }}>{section.title}</span>} key={idx}>
                                            <div className="mt-3">
                                                {section.type === 'nested_tabs' ? (
                                                    <Tabs defaultActiveKey={section.tabs[0].name} id={`subtabs-${idx}`} className="mb-3" variant="pills">
                                                        {section.tabs.map((tab, tIdx) => (
                                                            <Tab eventKey={tab.name} title={tab.name} key={tIdx}>
                                                                {renderTable(tab.data, idx, tIdx)}
                                                            </Tab>
                                                        ))}
                                                    </Tabs>
                                                ) : (
                                                    renderTable(section.data, idx)
                                                )}
                                            </div>
                                        </Tab>
                                    ))}
                                </Tabs>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Handle Empty Results or Error State display */}
            {results && !loading && Array.isArray(results) && results.length === 0 && (
                <Row>
                    <Col lg="12">
                        <Card><Card.Body><Alert variant="info">No results found.</Alert></Card.Body></Card>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default TradeSearch;